import cookies from 'browser-cookies';
import envData from '../../../config/env.json';

import type {
  ChallengeFile,
  ChallengeFiles,
  CompletedChallenge,
  DataChangeTourStatusProps,
  ImageProps,
  MessageProps,
  Product,
  SavedChallenge,
  SavedChallengeFile,
  User
} from '../redux/prop-types';
// eslint-disable-next-line import/no-cycle
import { NationalFormInput } from './api/quiz';
import { formatCPF } from './general-functions';

const { apiLocation } = envData as {
  apiLocation: string;
};

const base = apiLocation;

const defaultOptions: RequestInit = {
  credentials: 'include'
};

// csrf_token is passed to the client as a cookie. The client must send
// this back as a header.
function getCSRFToken() {
  const token =
    typeof window !== 'undefined' ? cookies.get('csrf_token') : null;
  return token ?? '';
}

export interface ResponseWithData<T> {
  response: Response;
  data: T;
}

async function getEcommerce(path: string) {
  const options: RequestInit = {
    method: 'GET'
  };

  const result = await fetch(`${apiLocation}/${path}`, options);
  const status = result.status;
  const data: unknown = await result.json();
  return {
    status,
    data
  };
}

// TODO: Might want to handle flash messages as close to the request as possible
// to make use of the Response object (message, status, etc)
async function get<T>(path: string): Promise<ResponseWithData<T>> {
  const response = await fetch(`${base}${path}`, defaultOptions);

  return combineDataWithResponse(response);
}

async function combineDataWithResponse<T>(response: Response) {
  const text = await response.text();

  if (!text) {
    return { response, data: {} as T };
  }

  try {
    const data = JSON.parse(text) as T;
    return { response, data };
  } catch (e) {
    console.error('Resposta não é JSON:', text);
    throw e;
  }
}

export function post<T = void>(
  path: string,
  body: unknown
): Promise<ResponseWithData<T>> {
  return request('POST', path, body);
}

export function postAuth<T = void>(
  path: string,
  body: unknown
): Promise<ResponseWithData<T>> {
  return requestWithoutCSRF('POST', path, body);
}

export function putAuth<T = void>(
  path: string,
  body: unknown
): Promise<ResponseWithData<T>> {
  return requestWithoutCSRF('PUT', path, body);
}

function put<T = void>(
  path: string,
  body?: unknown
): Promise<ResponseWithData<T>> {
  return request('PUT', path, body);
}

function deleteRequest<T = void>(
  path: string,
  body: unknown
): Promise<ResponseWithData<T>> {
  return request('DELETE', path, body);
}

async function request<T>(
  method: 'POST' | 'PUT' | 'DELETE',
  path: string,
  body: unknown
): Promise<ResponseWithData<T>> {
  const options: RequestInit = {
    ...defaultOptions,
    method,
    headers: {
      'CSRF-Token': getCSRFToken(),
      'Content-Type': 'application/json'
    },
    body: JSON.stringify(body)
  };

  const response = await fetch(`${base}${path}`, options);
  return combineDataWithResponse(response);
}

async function requestWithoutCSRF<T>(
  method: 'POST' | 'PUT' | 'DELETE',
  path: string,
  body: unknown
): Promise<ResponseWithData<T>> {
  const options: RequestInit = {
    ...defaultOptions,
    method,
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify(body)
  };

  const response = await fetch(`${base}${path}`, options);
  return combineDataWithResponse(response);
}

// ##############################################
// #### USER SETTINGS
// ##############################################

/** GET **/

interface SessionUser {
  user?: { [username: string]: User };
}

type CompleteChallengeFromApi = {
  files: Array<Omit<ChallengeFile, 'fileKey'> & { key: string }>;
} & Omit<CompletedChallenge, 'challengeFiles'>;

type SavedChallengeFromApi = {
  files: Array<Omit<SavedChallengeFile, 'fileKey'> & { key: string }>;
} & Omit<SavedChallenge, 'challengeFiles'>;

type ApiSessionResponse = Omit<SessionUser, 'user'>;
type ApiUser = {
  user: {
    [username: string]: Omit<
      User,
      'completedChallenges' & 'savedChallenges'
    > & {
      completedChallenges?: CompleteChallengeFromApi[];
      savedChallenges?: SavedChallengeFromApi[];
    };
  };
  result?: string;
};

type UserResponse = {
  user: { [username: string]: User } | Record<string, never>;
  result: string | undefined;
};

function parseApiResponseToClientUser(data: ApiUser): UserResponse {
  const userData = data.user?.[data?.result ?? ''];
  let completedChallenges: CompletedChallenge[] = [];
  let savedChallenges: SavedChallenge[] = [];
  if (userData) {
    completedChallenges = mapFilesToChallengeFiles(
      userData.completedChallenges
    );
    savedChallenges = mapFilesToChallengeFiles(userData.savedChallenges);
  }
  return {
    user: {
      [data.result ?? '']: { ...userData, completedChallenges, savedChallenges }
    },
    result: data.result
  };
}

// TODO: this at least needs a few aliases so it's human readable
// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export function mapFilesToChallengeFiles<File, Rest>(
  fileContainer: ({ files: (File & { key: string })[] } & Rest)[] = []
) {
  return fileContainer.map(({ files, ...rest }) => ({
    ...rest,
    challengeFiles: mapKeyToFileKey(files)
  }));
}

function mapKeyToFileKey<K>(
  files: (K & { key: string })[]
): (Omit<K, 'key'> & { fileKey: string })[] {
  return files.map(({ key, ...rest }) => ({ ...rest, fileKey: key }));
}

export function getSessionUser(): Promise<ResponseWithData<SessionUser>> {
  const responseWithData: Promise<
    ResponseWithData<ApiUser & ApiSessionResponse>
  > = get('/user/get-session-user');
  // TODO: Once DB is migrated, no longer need to parse `files` -> `challengeFiles` etc.
  return responseWithData.then(({ response, data }) => {
    const { result, user } = parseApiResponseToClientUser(data);
    return {
      response,
      data: {
        result,
        user
      }
    };
  });
}

type UserProfileResponse = {
  entities: Omit<UserResponse, 'result'>;
  result: string | undefined;
};
export function getUserProfile(
  username: string
): Promise<ResponseWithData<UserProfileResponse>> {
  const responseWithData = get<{ entities?: ApiUser; result?: string }>(
    `/api/users/get-public-profile?username=${username}`
  );
  return responseWithData.then(({ response, data }) => {
    const { result, user } = parseApiResponseToClientUser({
      user: data.entities?.user ?? {},
      result: data.result
    });
    return {
      response,
      data: {
        entities: { user },
        result
      }
    };
  });
}

type ResultEcommerce = {
  status: number;
  // eslint-disable-next-line @typescript-eslint/ban-types
  data: object;
};

type ResultSuccessEcommerce = {
  status: number;
  data: ResultSuccessData;
};

type ResultErrorEcommerce = {
  status: number;
  data: ResultErrorData;
};

type ResultSuccessData = {
  nivelAcesso?: string;
  turma?: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  produtos?: any[];
};

type ResultErrorData = {
  messages?: ErrorDataObj[];
};

type ErrorDataObj = {
  message: string;
  type: string;
};

export type ParseResultEcommerceProps = {
  type: string;
  title: string;
  message: string;
  nivelAcesso: string | undefined;
  turma: string | undefined;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  produtos?: any[];
};

function parseResultEcommerce(
  response: ResultEcommerce
): ParseResultEcommerceProps {
  switch (response.status) {
    case 401: {
      return {
        type: 'Error',
        title: 'Indisponivel',
        message: 'Serviço indisponivel no momento, tente novamente mais tarde.',
        nivelAcesso: undefined,
        turma: undefined
      };
    }
    case 200: {
      const responseSuccess: ResultSuccessEcommerce = response;
      return {
        type: 'Success',
        title: 'Sucesso!',
        message: 'Complemento de dados realizado com sucesso.',
        nivelAcesso: responseSuccess.data.nivelAcesso,
        turma: responseSuccess.data.turma,
        produtos: responseSuccess.data.produtos
      };
    }
    case 400: {
      const responseError: ResultErrorEcommerce = response;
      const { message, type } = responseError.data.messages[0];
      return {
        type: 'Error',
        title: type ?? '',
        message: message ?? '',
        nivelAcesso: undefined,
        turma: undefined
      };
    }
    default: {
      return {
        type: 'Error',
        title: 'Manutenção',
        message: 'Serviço em manutenção',
        nivelAcesso: undefined,
        turma: undefined
      };
    }
  }
}

interface Cert {
  certTitle: string;
  username: string;
  date: Date;
  completionTime: string;
}
export function getShowCert(
  username: string,
  certSlug: string
): Promise<ResponseWithData<Cert>> {
  return get(`/certificate/showCert/${username}/${certSlug}`);
}

export function getUsernameExists(
  username: string
): Promise<ResponseWithData<boolean>> {
  return get(`/api/users/exists?username=${username}`);
}

/** POST **/

interface Report {
  username: string;
  reportDescription: string;
}
export function postReportUser(body: Report): Promise<ResponseWithData<void>> {
  return post('/user/report-user', body);
}

export function postDeleteAccount(): Promise<ResponseWithData<void>> {
  return deleteRequest('/account/delete', {});
}

export function getIsUserDeletedAccount(): Promise<ResponseWithData<boolean>> {
  return get('/account/user-deleted');
}

export function postRevertDeleteAccount(): Promise<ResponseWithData<void>> {
  return post('/account/revert-delete', {});
}

export function postUserToken(): Promise<ResponseWithData<void>> {
  return post('/user/user-token', {});
}

export function postSaveChallenge(body: {
  id: string;
  files: ChallengeFiles;
}): Promise<ResponseWithData<void>> {
  return post('/save-challenge', body);
}

// ##############################################
// #### USER PROFILE
// ##############################################

/** PUT **/

interface MyAbout {
  name: string;
  location: string;
  about: string;
  picture: string;
}
export function putUpdateMyAbout(
  values: MyAbout
): Promise<ResponseWithData<void>> {
  return put('/update-my-about', { ...values });
}

export function putUpdateMyUsername(
  username: string
): Promise<ResponseWithData<void>> {
  return put('/update-my-username', { username });
}

export function putUpdateMyProfileUI(
  profileUI: User['profileUI']
): Promise<ResponseWithData<void>> {
  return put('/update-my-profileui', { profileUI });
}

export function putUpdateMySocials(
  update: Record<string, string>
): Promise<ResponseWithData<void>> {
  return put('/update-my-socials', update);
}

export function putUpdateMySound(
  update: Record<string, string>
): Promise<ResponseWithData<void>> {
  return put('/update-my-sound', update);
}

export function putUpdateMyTheme(
  update: Record<string, string>
): Promise<ResponseWithData<void>> {
  return put('/update-my-theme', update);
}

export function putUpdateMyKeyboardShortcuts(
  update: Record<string, string>
): Promise<ResponseWithData<void>> {
  return put('/update-my-keyboard-shortcuts', update);
}

export function putUpdateMyHonesty(
  update: Record<string, string>
): Promise<ResponseWithData<void>> {
  return put('/update-my-honesty', update);
}

export function putUpdateMyQuincyEmail(
  update: Record<string, string>
): Promise<ResponseWithData<void>> {
  return put('/update-my-quincy-email', update);
}

export function putUpdateMyPortfolio(
  update: Record<string, string>
): Promise<ResponseWithData<void>> {
  return put('/update-my-portfolio', update);
}

export function putUserAcceptsTerms(
  quincyEmails: boolean
): Promise<ResponseWithData<void>> {
  return put('/update-privacy-terms', { quincyEmails });
}

export function putUserUpdateEmail(
  email: string
): Promise<ResponseWithData<void>> {
  return put('/update-my-email', { email });
}

export function postCheckPassword(
  password: string
): Promise<ResponseWithData<void>> {
  return post('/check-password', { password });
}

export function putVerifyCert(
  certSlug: string
): Promise<ResponseWithData<void>> {
  return put('/certificate/verify', { certSlug });
}

/** DELETE **/
export function deleteUserToken(): Promise<ResponseWithData<void>> {
  return deleteRequest('/user/user-token', {});
}

// ##############################################
// #### AUTHENTICATION
// ##############################################

interface AuthLoginLocal {
  email: string;
  password: string;
}

export function postSignInLocal(
  formAuth: AuthLoginLocal
): Promise<ResponseWithData<void>> {
  return postAuth('/signin-local', { ...formAuth });
}

interface AuthSignUpLocal {
  name: string;
  phone: string;
  cpf: string;
  email: string;
  password: string;
}

export function postSignUpLocal(
  formAuth: AuthSignUpLocal
): Promise<ResponseWithData<void>> {
  return postAuth('/signup-local', { ...formAuth });
}

interface ForgotPassword {
  email: string;
}

export function postForgotPassword(
  formAuth: ForgotPassword
): Promise<ResponseWithData<void>> {
  return postAuth('/forgot-password', { ...formAuth });
}

interface FormRecoveryPassword {
  id: string;
  password: string;
  timeRequest: Date;
}

export function putRecoveryPassword(
  formRecovery: FormRecoveryPassword
): Promise<ResponseWithData<void>> {
  return putAuth('/recovery-password', { ...formRecovery });
}

export function putChangePassword(form: {
  currentPassword: string;
  newPassword: string;
}): Promise<ResponseWithData<void>> {
  return put('/change-password', { ...form });
}

export function putCreatePassword(form: {
  password: string;
}): Promise<ResponseWithData<void>> {
  return put('/create-password', { ...form });
}

export function postSendEmailVerification(
  formAuth: AuthLoginLocal
): Promise<ResponseWithData<void>> {
  return post('/email-verification', { ...formAuth });
}

interface VerificationEmail {
  id: string;
}

export function postConfirmVerificationEmail(
  idUser: VerificationEmail
): Promise<ResponseWithData<void>> {
  return post('/confirm-verification-email', { ...idUser });
}

// ##############################################
// #### E-COMMERCE
// ##############################################

export async function getAccessUserByEcommerce(cpf: string) {
  try {
    const response = await getEcommerce(`get-access-level?cpf=${cpf}`);
    return parseResultEcommerce(response);
  } catch (e) {
    console.log(e);
  }
}

interface MySubscription {
  name: string;
  cpf: string;
  email: string;
  phone: string;
}
export function putRegisterSubscription(
  values: MySubscription
): Promise<ResponseWithData<void>> {
  return put('/register-subscription', { ...values });
}

interface PaymentUser {
  access: string;
  source: string;
}
export function putRegisterPaymentUser(
  values: PaymentUser
): Promise<ResponseWithData<void>> {
  return put('/register-payment-user', { ...values });
}

// ##############################################
// #### UTM
// ##############################################

export function putUtmData(values: unknown): Promise<ResponseWithData<void>> {
  return put('/register-utm-data', { ...values });
}

// ##############################################
// #### TOUR
// ##############################################

export async function putChangeTourStatus(
  tour: DataChangeTourStatusProps
): Promise<ResponseWithData<void>> {
  return await put('/change-tour-status', tour);
}

// ##############################################
// #### USER ANSWERS
// ##############################################

interface AnswerProp {
  id: string;
  answer: number | string;
}

export function postRegisterUserAnswer(
  payload: AnswerProp
): Promise<ResponseWithData<void>> {
  return post('/register-user-answer', payload);
}

// ##############################################
// #### INTEGRAÇÃO SGE: PROJETO NACIONAL
// ##############################################

export type RegistrationResponseProps = {
  user: {
    id: string;
    ra: string;
    name: string;
    cpf: string;
    birthDate: string;
    sex: string;
    homeCountry: string;
    homeState: string;
    homeCity: string;
    email: string;
    educationLevel: string;
    race: string;
    country: string;
    nationality: string;
    state: string;
    city: string;
    phone: string;
    cel: string;
    occupationalSituation: string;
    street: string;
    number: string;
    complement: string;
    neighborhood: string;
    cep: string;
  };
};

export async function getCheckUserFirstRegistration(
  cpf: string
): Promise<ResponseWithData<RegistrationResponseProps>> {
  const responseWithData: Promise<ResponseWithData<RegistrationResponseProps>> =
    get(`/nacional?cpf=${formatCPF(cpf)}`);

  return responseWithData.then(response => ({
    response: response.response,
    data: response.data
  }));
}

export async function completeNacionalUser(
  params: NationalFormInput
): Promise<ResponseWithData<void>> {
  const responseWithData: Promise<ResponseWithData<void>> = put(
    '/nacional/complete-register',
    params
  );

  return responseWithData.then(({ response, data }) => {
    return {
      response,
      data
    };
  });
}

// ##############################################
// #### PLATFORM MESSAGES
// ##############################################

export function postCreateMessage(
  payload: MessageProps
): Promise<ResponseWithData<void>> {
  const response = post('/api/messages/create-message', {
    messagePayload: payload
  });
  return response;
}

export interface MessageDataResponse<T> {
  status: number;
  message: string;
  result: T;
  error: boolean;
  success: boolean;
}
export async function getMessageListForUser(
  userId: string
): Promise<{ messageList: MessageProps[] }> {
  const responseWithData: Promise<
    ResponseWithData<{
      result: MessageDataResponse<{ messageList: MessageProps[] }>;
    }>
  > = get(`/api/messages/get-messages-for-user?userId=${userId}`);

  return await responseWithData.then(({ data }) => {
    return { messageList: data.result.result.messageList };
  });
}

export type FilterOptionsProps = {
  pageNumber: number;
  pageSize: number;
  search?: string;
  total?: number;
  sort?: string;
  order?: 'ASC' | `DESC`;
};

type FilterProps = {
  filter?: FilterOptionsProps;
};

function buildQuery(filter: FilterOptionsProps): string {
  if (!filter) {
    return '';
  }

  const { pageNumber, pageSize, search, sort, order } = filter;

  const query = {
    ...(pageNumber && { pageNumber }),
    ...(pageSize && { pageSize }),
    ...(search && { search }),
    ...(sort && { sort }),
    ...(order && { order })
  };

  return Object.entries(query)
    .map(([key, value]) => `${key}=${value}`)
    .join('&');
}

interface MessageInfoProps {
  messageList: MessageProps[];
  messageListSize: number;
  pageNumber: number;
  pageSize: number;
  totalPages: number;
}
export async function getMessages({
  filter
}: FilterProps): Promise<MessageInfoProps> {
  const query: string = buildQuery(filter as FilterOptionsProps);

  const responseWithData: Promise<
    ResponseWithData<{ result: MessageDataResponse<MessageInfoProps> }>
  > = get(`/api/messages/get-messages?${query}`);

  return responseWithData.then(({ data }) => {
    const paginationResult = data?.result.result;
    return {
      messageList: paginationResult?.messageList ?? [],
      messageListSize: paginationResult?.messageListSize ?? 0,
      pageNumber: paginationResult?.pageNumber,
      pageSize: paginationResult?.pageSize,
      totalPages: paginationResult?.totalPages
    };
  });
}

export async function getMessageById(messageId: string): Promise<MessageProps> {
  const responseWithData: Promise<
    ResponseWithData<{ result: MessageDataResponse<{ message: MessageProps }> }>
  > = get(`/api/messages/get-message-by-id?messageId=${messageId}`);

  return await responseWithData.then(({ data }) => {
    return data.result.result.message;
  });
}

export async function postUpdateMessage(
  body: MessageProps
): Promise<ResponseWithData<void>> {
  const { id, ...updatedBody } = body;
  return put(`/api/messages/update-message?messageId=${id ?? ''}`, updatedBody);
}

async function fileUpload<T>(
  path: string,
  body: BodyInit
): Promise<ResponseWithData<T>> {
  const options: RequestInit = {
    ...defaultOptions,
    method: 'POST',
    headers: {
      'CSRF-Token': getCSRFToken()
    },
    body
  };

  const response = await fetch(`${base}${path}`, options);
  return combineDataWithResponse(response);
}

export function postUploadImage(
  image: File
): Promise<ResponseWithData<MessageDataResponse<{ metadata: ImageProps }>>> {
  const formData = new FormData();
  formData.append('image', image);

  return fileUpload('/upload-image', formData);
}

export function postUploadPublicImage(
  image: File
): Promise<ResponseWithData<MessageDataResponse<{ fileUrl: string }>>> {
  const formData = new FormData();
  formData.append('image', image);

  return fileUpload('/upload-public-image', formData);
}

export function deleteMessageById(
  id: string
): Promise<ResponseWithData<{ result: { count: number } }>> {
  return deleteRequest(`/api/messages/delete-by-id/${id}`, {});
}

export async function getImageLink(
  userId: string,
  action: string,
  filePath: string
): Promise<string> {
  const responseWithData = await get<{ result: { imageLink: string } }>(
    `/get-image-link/${userId}/generate-link?action=${action}&filePath=${filePath}`
  );
  return responseWithData.data.result.imageLink;
}

export type SourcesResponse = {
  sources: { identifier: string; name: string; modality: string }[];
};

export async function getClassList(): Promise<
  ResponseWithData<{ result: SourcesResponse }>
> {
  const responseWithData = await get<{ result: SourcesResponse }>(
    '/users/get-sources'
  );

  return responseWithData;
}

// ##############################################
// #### BLOCK INACTIVE USERS
// ##############################################

export async function checkUserInactiveStatus(
  userId: string
): Promise<boolean> {
  const responseWithData: Promise<
    ResponseWithData<{ result: { status: 'active' | 'blocked' } }>
  > = get(`/api/users/status/${userId}`);

  const { data } = await responseWithData;

  return data.result.status !== 'active';
}

export async function checkUserInactiveStatusByEmail(
  email: string
): Promise<string> {
  const responseWithData: Promise<
    ResponseWithData<{
      result: { result: { status: 'active' | 'blocked' } };
    }>
  > = get(`/api/users/status-by-email?email=${email}`);

  const { data } = await responseWithData;

  return data.result.result.status;
}

interface StandardReponse<T> {
  message: string;
  status: number;
  success: boolean;
  result: T;
}

export type UnlockUserAccountResponse = StandardReponse<{
  message: string;
  userId: string;
}>;

type HasPasswordResponse = {
  hasPassword: {
    message: string;
    status: number;
    success: boolean;
    result: { hasPassword: boolean };
  };
};

export async function unlockUserAccount(body: {
  cpf: string;
  password: string;
}): Promise<ResponseWithData<UnlockUserAccountResponse>> {
  const responseWithData: Promise<ResponseWithData<UnlockUserAccountResponse>> =
    post('/api/users/unlock-user-account', body);

  return await responseWithData;
}

export async function getUserHasPassword(
  cpf: string
): Promise<HasPasswordResponse> {
  try {
    const responseWithData: ResponseWithData<HasPasswordResponse> = await get(
      `/api/users/has-password?cpf=${cpf}`
    );

    return responseWithData.data;
  } catch (error) {
    console.error('Error:', error);
    return {
      hasPassword: {
        message: 'Erro ao verificar senha',
        status: 500,
        success: false,
        result: { hasPassword: false }
      }
    };
  }
}

export async function getNationalDepartmentMetrics(): Promise<void> {
  try {
    const response = await fetch(
      `${base}/api/admin/report/national-departament-metrics`,
      defaultOptions
    );
    const blob = await response.blob();

    // Create a URL for the blob
    const url = window.URL.createObjectURL(blob);
    // Create a temporary link element
    const link = document.createElement('a');
    link.href = url;
    link.download = 'relatorio-metricas-nacionais.pdf';
    // Append link to body, click it, and remove it
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    // Clean up the URL
    window.URL.revokeObjectURL(url);
  } catch (error) {
    console.error('Error downloading national department metrics:', error);
    throw error;
  }
}

// Estados brasileiros para relatórios de usuários inativos
const nacionalDepartaments = {
  PR: { uf: 'PR', name: 'Paraná' },
  PI: { uf: 'PI', name: 'Piauí' },
  RR: { uf: 'RR', name: 'Roraima' },
  CE: { uf: 'CE', name: 'Ceará' },
  AM: { uf: 'AM', name: 'Amazonas' },
  RS: { uf: 'RS', name: 'Rio Grande do Sul' },
  BA: { uf: 'BA', name: 'Bahia' },
  RN: { uf: 'RN', name: 'Rio Grande do Norte' }
} as const;

export async function getInactiveUsers(): Promise<void> {
  try {
    // Criar array de promessas para todos os estados
    const fetchPromises = Object.values(nacionalDepartaments).map(
      nacionalDepartament =>
        fetch(
          `${base}/api/admin/report/inactive-users?group=nacional-${nacionalDepartament.uf.toLowerCase()}`,
          defaultOptions
        )
    );

    // Fetch data for all states
    const responses = await Promise.all(fetchPromises);

    // Get the blobs from all responses
    const blobs = await Promise.all(responses.map(response => response.blob()));

    // Function to download a single file
    const downloadFile = (blob: Blob, filename: string) => {
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    };

    // Download all files
    Object.values(nacionalDepartaments).forEach(
      (nacionalDepartament, index) => {
        downloadFile(
          blobs[index],
          `usuarios-inativos-${nacionalDepartament.uf.toLowerCase()}.csv`
        );
      }
    );
  } catch (error) {
    console.error('Error downloading inactive users:', error);
    throw error;
  }
}

export async function getProducts(): Promise<
  ResponseWithData<{
    result: { result: { products: Product[] } };
  }>
> {
  const responseWithData = await get<{
    result: { result: { products: Product[] } };
  }>('/api/products');

  return responseWithData;
}

export async function postLinkedProduct(body: {
  idSGN: number;
  idProductDevstart: string;
  title: string;
  version: number;
}): Promise<ResponseWithData<void>> {
  const responseWithData = await post(`/api/product/link-product`, body);

  return responseWithData;
}

export async function postSyncProducts() {
  const responseWithData = await post(`/api/product/sync-sgn`, {});

  return responseWithData;
}

// setAiAssistantSession foi movido para @utils/api/ai-assistant.ts

interface FilterDiscordLogs {
  search?: string;
  sort?: string;
  order?: string;
  source?: string[];
  userRole?: string[];
  status?: string[];
}

export async function exportDiscordLogsAPI(filters: FilterDiscordLogs) {
  const queryParams = new URLSearchParams();

  if (filters.search) queryParams.append('search', filters.search);
  if (filters.sort) queryParams.append('sort', filters.sort);
  if (filters.order) queryParams.append('order', filters.order);
  if (filters.source?.length) {
    queryParams.append('source', filters.source.join(','));
  }
  if (filters.userRole?.length) {
    queryParams.append('userRole', filters.userRole.join(','));
  }
  if (filters.status?.length) {
    queryParams.append('status', filters.status.join(','));
  }

  const URL = `/api/discord/logs/export?${queryParams.toString()}`;
  const responseWithData = await fetch(`${base}${URL}`, {
    headers: {
      Accept: 'text/csv'
    },
    method: 'GET',
    credentials: 'include'
  });

  return responseWithData;
}
